// EMBERS — Values Fingerprint
// A calm, distinctive, generative visual that encodes the four signals.
// Designed to be printed, saved, contemplated. Not gamified.

import type { ForkChoice, ValuesFingerprintParams } from './types'

export function generateFingerprintSVG(params: ValuesFingerprintParams, size = 520): string {
  const { cooperation, fork, reciprocityBalance, normWeight, normText } = params
  const s = size
  const cx = s / 2
  const cy = s / 2

  const ember = '#E07A3D'
  const emberDark = '#C25E2A'
  const cream = '#F5F0E6'
  const charcoal = '#1C1917'
  const warmGray = '#6B6259'
  const deep = '#121110'

  // Map signals to visual properties
  const reach = 0.58 + cooperation * 0.32          // outer radius of the "willingness" ring
  const inner = 0.22 + (1 - cooperation) * 0.08    // how tight the core feels

  // Fork direction + mark style
  const forkAngle = getForkAngle(fork)
  const forkMark = getForkMark(fork)

  // Reciprocity: -1 (gave too much) tilts left, +1 (received) tilts right. 0 is balanced.
  const recipTilt = reciprocityBalance * 14        // degrees of gentle lean on the horizon line

  // Norm weight affects the density / presence of the central ember cluster and the word weight
  const normPresence = Math.max(0.35, Math.min(0.95, normWeight))
  const normDisplay = (normText || 'the norm at stake').slice(0, 48)

  // Build the SVG
  const elements: string[] = []

  // Deep quiet background
  elements.push(`<rect x="0" y="0" width="${s}" height="${s}" fill="${deep}"/>`)

  // Very subtle paper texture rings (hand-made signal fire feeling)
  for (let i = 0; i < 5; i++) {
    const r = (0.38 + i * 0.07) * s
    elements.push(`<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${charcoal}" stroke-width="1" opacity="0.6"/>`)
  }

  // Outer willingness ring — how far cooperation was extended
  const outerR = reach * s * 0.5
  elements.push(`<circle cx="${cx}" cy="${cy}" r="${outerR}" fill="none" stroke="${ember}" stroke-width="1.5" opacity="0.35"/>`)
  elements.push(`<circle cx="${cx}" cy="${cy}" r="${outerR * 0.985}" fill="none" stroke="${ember}" stroke-width="0.75" opacity="0.18"/>`)

  // The "embers" — small living points whose number and spread reflect cooperation + norm weight
  const emberCount = Math.floor(7 + cooperation * 11 + normPresence * 6)
  for (let i = 0; i < emberCount; i++) {
    const a = (i / emberCount) * Math.PI * 2 + (i % 3) * 0.07
    const dist = outerR * (0.78 + ((i % 5) - 2) * 0.028)
    const x = cx + Math.cos(a) * dist
    const y = cy + Math.sin(a) * dist
    const r = 1.6 + (i % 3) * 0.5
    const op = 0.35 + (normPresence - 0.4) * 0.4
    elements.push(`<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r}" fill="${ember}" opacity="${op}"/>`)
  }

  // Horizon line of reciprocity — gently tilted by the felt imbalance
  const horizonY = cy + 6
  const tiltRad = (recipTilt * Math.PI) / 180
  const hLen = outerR * 0.92
  const x1 = cx - hLen * Math.cos(tiltRad)
  const y1 = horizonY - hLen * Math.sin(tiltRad)
  const x2 = cx + hLen * Math.cos(tiltRad)
  const y2 = horizonY + hLen * Math.sin(tiltRad)
  elements.push(`<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${warmGray}" stroke-width="1.25" stroke-opacity="0.7"/>`)

  // Small dots on the horizon showing "what I gave" vs "what came toward me"
  const gaveX = cx - (reciprocityBalance < 0 ? Math.abs(reciprocityBalance) * 38 : 18)
  const recvX = cx + (reciprocityBalance > 0 ? Math.abs(reciprocityBalance) * 38 : 18)
  elements.push(`<circle cx="${gaveX.toFixed(1)}" cy="${horizonY.toFixed(1)}" r="3" fill="${emberDark}"/>`)
  elements.push(`<circle cx="${recvX.toFixed(1)}" cy="${horizonY.toFixed(1)}" r="3" fill="${cream}" fill-opacity="0.85"/>`)

  // The Fork mark — the most emotionally charged visual element
  // A distinct "char" or directional scar placed according to the choice
  const forkDist = outerR * 0.61
  const fx = cx + Math.cos(forkAngle) * forkDist
  const fy = cy + Math.sin(forkAngle) * forkDist

  if (forkMark === 'scar') {
    // Withdraw / protect — a quiet break in the ring
    elements.push(`<line x1="${(fx - 11).toFixed(1)}" y1="${(fy - 9).toFixed(1)}" x2="${(fx + 11).toFixed(1)}" y2="${(fy + 9).toFixed(1)}" stroke="${ember}" stroke-width="2.5" stroke-opacity="0.9"/>`)
    elements.push(`<circle cx="${fx.toFixed(1)}" cy="${fy.toFixed(1)}" r="2.5" fill="${deep}"/>`)
  } else if (forkMark === 'bridge') {
    // Name + repair — two small lines reaching toward each other
    elements.push(`<line x1="${(fx - 13).toFixed(1)}" y1="${(fy - 2).toFixed(1)}" x2="${(fx - 2).toFixed(1)}" y2="${fy.toFixed(1)}" stroke="${ember}" stroke-width="2"/>`)
    elements.push(`<line x1="${(fx + 2).toFixed(1)}" y1="${fy.toFixed(1)}" x2="${(fx + 13).toFixed(1)}" y2="${(fy - 2).toFixed(1)}" stroke="${cream}" stroke-width="2" stroke-opacity="0.9"/>`)
  } else if (forkMark === 'open') {
    // Contextual grace — an open arc, soft
    elements.push(`<path d="M ${fx - 10},${fy - 7} Q ${fx},${fy + 4} ${fx + 10},${fy - 7}" fill="none" stroke="${ember}" stroke-width="2.25" stroke-opacity="0.85"/>`)
  } else {
    // Enforce boundary — a small solid bar with weight
    elements.push(`<rect x="${(fx - 12).toFixed(1)}" y="${(fy - 2.5).toFixed(1)}" width="24" height="5" rx="1" fill="${ember}" fill-opacity="0.85"/>`)
  }

  // Central living coal cluster — density reflects how much this norm currently matters
  const coreR = inner * s * 0.5
  elements.push(`<circle cx="${cx}" cy="${cy}" r="${coreR}" fill="${charcoal}" stroke="${ember}" stroke-width="1" stroke-opacity="${0.3 + normPresence * 0.35}"/>`)

  // Inner ember glow points (the thing you are protecting / the value you felt)
  const innerEmbers = Math.floor(3 + normPresence * 5)
  for (let i = 0; i < innerEmbers; i++) {
    const a = (i * 2.3) + 0.8
    const ix = cx + Math.cos(a) * (coreR * 0.55)
    const iy = cy + Math.sin(a) * (coreR * 0.55)
    elements.push(`<circle cx="${ix.toFixed(1)}" cy="${iy.toFixed(1)}" r="1.8" fill="${ember}" opacity="${0.5 + normPresence * 0.4}"/>`)
  }

  // The named norm, rendered quietly in the center — the heart of the fingerprint
  const normFontSize = 10.5 + normPresence * 2.5
  elements.push(`
    <text x="${cx}" y="${cy + 3}" 
          text-anchor="middle" 
          dominant-baseline="middle" 
          font-family="ui-serif, Georgia, 'Times New Roman', serif" 
          font-size="${normFontSize}" 
          fill="${cream}" 
          fill-opacity="${0.65 + normPresence * 0.25}"
          letter-spacing="0.3">${escapeXml(normDisplay)}</text>
  `)

  // Very subtle outer label
  elements.push(`<text x="${cx}" y="${s - 28}" text-anchor="middle" font-family="ui-sans-serif, system-ui" font-size="8.5" fill="${warmGray}" fill-opacity="0.5">VALUES FINGERPRINT • EMBERS</text>`)

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 ${s} ${s}">\n  ${elements.join('\n  ')}\n</svg>`
  return svg
}

function getForkAngle(fork: ForkChoice): number {
  // Place the fork mark in a consistent but meaningful cardinal direction
  switch (fork) {
    case 'withdraw_protect': return -2.1 // upper leftish — turning away
    case 'name_repair': return 2.6       // lower right — reaching across
    case 'contextual_grace': return 0.6  // gentle right — opening
    case 'enforce_boundary': return -1.35 // upper — standing ground
  }
}

function getForkMark(fork: ForkChoice): 'scar' | 'bridge' | 'open' | 'bar' {
  switch (fork) {
    case 'withdraw_protect': return 'scar'
    case 'name_repair': return 'bridge'
    case 'contextual_grace': return 'open'
    case 'enforce_boundary': return 'bar'
  }
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// Convert SVG string to a high-quality PNG data URL using canvas (for exports)
export async function svgToPngDataUrl(svg: string, size = 520): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)

    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d', { alpha: true })!
      ctx.fillStyle = '#121110'
      ctx.fillRect(0, 0, size, size)
      ctx.drawImage(img, 0, 0, size, size)
      URL.revokeObjectURL(url)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = reject
    img.src = url
  })
}

// Derive a simple reciprocity balance number from the two text fields the user wrote.
// This is intentionally interpretive and low-stakes — the user can override the visual if they want.
export function deriveReciprocityBalance(offered: string, received: string): number {
  const o = offered.trim().length
  const r = received.trim().length
  if (o === 0 && r === 0) return 0
  const diff = (r - o) / Math.max(28, o + r)
  return Math.max(-1, Math.min(1, diff * 1.6))
}
